import { GraphQLError } from 'graphql';

import { BulkReplacementRadio } from '../components/BulkReplacementModeChooser/BulkReplacementModeChooser';

import { AudienceTargetingsValue } from './audienceTargetings';
import {
    NumberRangeInputValues,
    PageStatus,
    RegionId,
    ValidationErrorData,
    ValidationErrorsData
} from './common';
import { DisplayPageBlock } from './displayMap';
import { CampaignType, GroupType } from './domain';
import { FeedCategoryNode } from './feedCategories';
import {
    GdBidModifierType,
    GdBusinessType,
    GdCampaignPlatform,
    GdCampaignStrategyType,
    GdFeedType,
    GdShowConditionAutobudgetPriority,
    GdSmartFilterConditionOperator,
    GdSmartFilterTab,
    GdSmartFilterTargetFunnel,
    GdUpdateStatus,
    GroupsQueryForEdit_client_feeds_rowset_categories,
    ValidationError,
    ValidationWarning
} from './graphql-grid';
import { GroupId } from './groups';
import {
    TargetingBrowserEngines,
    TargetingBrowserNames,
    TargetingDeviceNames,
    TargetingDeviceVendors,
    TargetingInterfaceLangs,
    TargetingInternalNetwork,
    TargetingIsBrowser,
    TargetingIsMobile,
    TargetingIsTablet,
    TargetingIsTouch,
    TargetingOsFamilies,
    TargetingOsNames,
    TargetingQueryReferers,
    TargetingShowDates,
    TargetingUserAgents,
    TargetingYandexUids
} from './internalGroups';
import { MinusPhrasesShortLibraryItem } from './minusPhraseLibrary';
import {
    RateCorrection,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsValue
} from './ratesCorrections';
import { EditPageRecommendationsData } from './recommendations';
import { RetargetingConditionData } from './retargeting';
import { UserProfileRetargetingCondition } from './userProfileRetargeting';
import { Omit } from './utils';

export interface EditGroupsState {
    pageStatus: PageStatus;
    groupsIds: GroupId[];
    campaignsIds: Array<string>;
    isNew: boolean;
    isNewEmptyGroup: boolean; // пустая только что созданная группа - после ее сохранения переходим на создание баннеров
    errors: (string | ValidationErrorData)[];
    groups: Record<GroupId, EditGroupData>;
    recommendations: Record<GroupId, EditPageRecommendationsData>;
    feedsIds: string[];
    feeds: Feeds;
    groupFilterInspector: GroupFilterInspectorState;
    isPerformanceFeedModalOpen: boolean;
    feedsPopupFetchFirstRun: boolean;
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>;
    retargetingConditions?: Array<RetargetingConditionData>;
    rateCorrectionsConstants: RatesCorrectionsConstantsData;
    keywordsCountLimit: number;
    reqId?: string;
    productsList?: ProductsList;
}

export enum MassActionId {
    groupsEdit = 'groupsEdit'
}

export type GroupsEditMassActionsState = Record<
    MassActionId,
    GroupsEditMassActions
>;

export interface GroupsEditMassActions {
    visibleModalActions: VisibleGroupsModalActions;
    data: GroupsEditMassActionsData;
}

export interface GroupsEditMassActionsData {
    minusPhrasesRadio: BulkReplacementRadio;
}

export interface VisibleGroupsModalActions {
    minusPhraseModal: boolean;
}

export type FeedCategory = Omit<
    GroupsQueryForEdit_client_feeds_rowset_categories,
    '__typename'
>;

export interface Feed {
    id: string;
    name: string;
    updateStatus: GdUpdateStatus;
    businessType: GdBusinessType;
    categories: Array<FeedCategory>;
    feedType?: GdFeedType;
}

export interface Feeds {
    [key: string]: Feed;
}

export interface EditGroupKeywords {
    id?: string;
    phrase: string;
    minusWords: Array<string>;
}

export interface KeywordsEditor {
    value: EditGroupKeywords[];
    input: string;
    isOpen: boolean;
}

export interface EditGroupAutotargeting {
    id?: string;
    isActive: boolean;
}

export type RegionSelectedMap = Record<RegionId, boolean>;

export interface EditGroupRegions {
    // имеется договоренность с бекендом о том что будем отправлять не только regionIds, но и selectedMap
    selectedMap: RegionSelectedMap;
    regionIds: RegionId[];
}

export interface FiltersEditor {
    isOpen: boolean;
    isLoading: boolean;
    auxiliaryFilter: string;
}

export interface EditBaseGroupDataValue {
    name: string;
    campaignType: undefined;
    groupType: GroupType;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
}

export interface RetargetingValue {
    id: string;
    retargetingId?: string;
}

export interface ShowConditionsRecommendationDataState {
    wasClosed: boolean;
}

export interface EditTextGroupDataValue {
    name: string;
    campaignType: CampaignType.TEXT;
    groupType: GroupType;
    autotargeting: EditGroupAutotargeting;
    retargeting: Array<RetargetingValue>;
    keywords: EditGroupKeywords[];
    minusKeywords: string[];
    rateCorrections: RatesCorrectionsValue;
    regions: EditGroupRegions;
    generalRate?: number;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
}

export interface EditPerformanceGroupDataValue {
    name: string;
    campaignType: CampaignType.PERFORMANCE;
    groupType: GroupType;
    hrefParams: string;
    bannerHeadersFeedField: string;
    bannerTextFeedField: string;
    regions: EditGroupRegions;
    minusKeywords: string[];
    rateCorrections: Record<RateCorrectionType, RateCorrection>;
    feedsPopupIsOpen: boolean;
    searchFeedFieldValue: string;
    selectedFeedId: string;
    feedId: string;
    filters: Array<EditGroupFilterItem>;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
}

export interface EditInternalGroupDataValue {
    name: string;
    regions: EditGroupRegions;
    campaignType: CampaignType.INTERNAL_DISTRIB | CampaignType.INTERNAL_FREE;
    groupType: GroupType;
    targetingYandexUids?: TargetingYandexUids;
    targetingInternalNetwork?: TargetingInternalNetwork;
    targetingIsBrowser?: TargetingIsBrowser;
    targetingIsTouch?: TargetingIsTouch;
    targetingIsTablet?: TargetingIsTablet;
    targetingIsMobile?: TargetingIsMobile;
    targetingOsNames?: TargetingOsNames;
    targetingOsFamilies?: TargetingOsFamilies;
    targetingBrowserNames?: TargetingBrowserNames;
    targetingBrowserEngines?: TargetingBrowserEngines;
    targetingDeviceNames?: TargetingDeviceNames;
    targetingDeviceVendors?: TargetingDeviceVendors;
    targetingQueryReferers?: TargetingQueryReferers;
    targetingInterfaceLangs?: TargetingInterfaceLangs;
    targetingUserAgents?: TargetingUserAgents;
    targetingShowDates?: TargetingShowDates;
    startTime?: GraphQlLocalDateTime;
    finishTime?: GraphQlLocalDateTime;
    level?: string;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    isAdditionalTargetingInternalOpen: boolean;
}

export enum CpmGroupShowConditionType {
    USER_PROFILE = 'USER_PROFILE',
    KEYWORD = 'KEYWORD'
}

export interface EditCpmGroupDataValue {
    name: string;
    campaignType: CampaignType.CPM_BANNER;
    groupType: GroupType;
    autotargeting: EditGroupAutotargeting;
    retargetingCondition?: UserProfileRetargetingCondition;
    keywords?: EditGroupKeywords[];
    showConditionType: CpmGroupShowConditionType;
    rateCorrections: RatesCorrectionsValue;
    rateCurrentType: RateCorrectionType;
    regions: EditGroupRegions;
    generalRate?: number;
    selectedLibraryList?: MinusPhrasesShortLibraryItem[];
    outdoorMap?: {
        pageBlocks: DisplayPageBlock[];
    };
    indoorMap?: {
        pageBlocks: DisplayPageBlock[];
    };
    audienceTargetings: AudienceTargetingsValue;
    audienceTargetingsTemplateName: string;
    minusKeywords?: string[];
    audienceCollection?: AudienceCollection[];
    productRestrictionId: string;
}

export type EditGroupDataValue =
    | EditBaseGroupDataValue
    | EditTextGroupDataValue
    | EditPerformanceGroupDataValue
    | EditInternalGroupDataValue
    | EditCpmGroupDataValue;

export interface EditGroupData {
    id: GroupId;
    campaign: {
        name: string;
        id: string;
        strategyType: GdCampaignStrategyType;
        platform?: GdCampaignPlatform;
        strategy: {
            isAutoBudget: boolean;
        };
    };
    serverValue: EditGroupDataValue;
    value: EditGroupDataValue;
    keywordsEditor: KeywordsEditor;
    filtersEditor: FiltersEditor;
    hasGeneralRate: boolean;
    isEditable: boolean;
    isSaved: boolean;
    errors: ValidationErrorsData;
    bannersCount: number;
    isNew: boolean;
    showConditionsRecommendationData: ShowConditionsRecommendationDataState;
}

export interface EditInternalGroupData extends EditGroupData {
    serverValue: EditInternalGroupDataValue;
    value: EditInternalGroupDataValue;
}

export enum AudienceCollection {
    COMPLETE = 'COMPLETE',
    FIRST_QUARTILE = 'FIRST_QUARTILE',
    MIDPOINT = 'MIDPOINT',
    START = 'START',
    THIRD_QUARTILE = 'THIRD_QUARTILE'
}

export interface AudienceCollectionValue {
    value: AudienceCollection;
    text: string;
}

export interface EditGroupSessionValue {
    groupsIds: Array<GroupId>;
    campaignsIds: Array<string>;
    isNew: boolean;
    isNewEmptyGroup: boolean;
}

export type SaveGroupResultData = {
    updated: Array<{ adGroupId: GroupId }>;
    errors: Array<Omit<ValidationError, '__typename'>>;
    warnings?: Array<Omit<ValidationWarning, '__typename'>>;
};

export type SaveGroupsResult = {
    responseErrors?: ReadonlyArray<GraphQLError>;
    data?: SaveGroupResultData;
};

export interface UpdateDataForEditGroups {
    groups: Record<string, EditGroupData>;
    retargetingConditions?: RetargetingConditionData[];
    reqId?: string;
}

export interface UpdateRetargetingConditionsForGroupsEdit {
    retargetingConditions: RetargetingConditionData[];
}

export interface DataForEditGroups {
    groups: Record<string, EditGroupData>;
    feedsIds: string[];
    feeds: Feeds;
    isLibraryListsFetched?: boolean;
    allowedRateCorrectionsData?: Record<GroupType, Array<GdBidModifierType>>;
    rateCorrectionsConstants?: RatesCorrectionsConstantsData;
    retargetingConditions?: RetargetingConditionData[];
    keywordsCountLimit: number;
    reqId?: string;
}

export interface DataForEditInternalGroups extends DataForEditGroups {
    groups: Record<string, EditInternalGroupData>;
}

export interface FeedFilterCampaign {
    strategy?: GdCampaignStrategyType;
    platform?: GdCampaignPlatform;
}

export interface EditGroupFilterItem {
    name: string;
    type: GdSmartFilterTargetFunnel;
    priceCpa?: number;
    priceCpc?: number;
    autobudgetPriority?: GdShowConditionAutobudgetPriority;
    id: string;
    tab: GdSmartFilterTab;
    conditions: Array<FeedFilterCondition>;
    feedCategories: Array<FeedCategoryNode>;
    categoryIds: Array<string>;
    isAvailable: boolean;
    vendors: string;
    priceRange: NumberRangeInputValues;
    campaign: FeedFilterCampaign;
    errors?: ValidationErrorsData;
}

export interface FeedFilterCondition {
    field: string;
    operator: GdSmartFilterConditionOperator;
    arguments: FeedFilterOperationControlValues;
    id: string;
    errors?: Array<ValidationErrorData>;
}

export interface FeedFilterSchemaOperator {
    type: GdSmartFilterConditionOperator;
    maxItems: number;
}

export enum FeedFilterSchemaFieldType {
    NUMBER = 'NUMBER',
    STRING = 'STRING',
    ENUM = 'ENUM'
}

export interface FeedFilterNumberFieldParams {
    min: number;
    max: number;
    precision: number;
}

export interface FeedFilterStringFieldParams {
    minLength: number;
    maxLength: number;
    pattern?: string;
}

export interface FeedFilterEnumFieldParams {
    values: Array<string>;
}

interface FeedFilterSchemaFieldBase {
    name: string;
    fieldType: FeedFilterSchemaFieldType;
    operators: Array<FeedFilterSchemaOperator>;
}

export interface FeedFilterSchemaNumberField
    extends FeedFilterSchemaFieldBase,
        FeedFilterNumberFieldParams {
    fieldType: FeedFilterSchemaFieldType.NUMBER;
}

export interface FeedFilterSchemaStringField
    extends FeedFilterSchemaFieldBase,
        FeedFilterStringFieldParams {
    fieldType: FeedFilterSchemaFieldType.STRING;
}

export interface FeedFilterSchemaEnumField
    extends FeedFilterSchemaFieldBase,
        FeedFilterEnumFieldParams {
    fieldType: FeedFilterSchemaFieldType.ENUM;
}

export type FeedFilterSchemaField =
    | FeedFilterSchemaNumberField
    | FeedFilterSchemaStringField
    | FeedFilterSchemaEnumField;

export interface FeedFilterSchema {
    fields: Array<FeedFilterSchemaField>;
}

export type NumberCondition =
    | NumberGreaterThanCondition
    | NumberLessThanCondition
    | NumberRangeCondition
    | NumberEqualsAnyCondition
    | NumberExistsCondition;

export interface NumberGreaterThanCondition {
    operator: GdSmartFilterConditionOperator.GREATER_THAN;
    arguments: Array<number | undefined>;
}

export interface NumberLessThanCondition {
    operator: GdSmartFilterConditionOperator.LESS_THAN;
    arguments: Array<number | undefined>;
}

export interface NumberRangeCondition {
    operator: GdSmartFilterConditionOperator.RANGE;
    arguments: Array<NumberRangeInputValues>;
}

export interface NumberEqualsAnyCondition {
    operator: GdSmartFilterConditionOperator.EQUALS_ANY;
    arguments: Array<string>;
}

export interface NumberExistsCondition {
    operator: GdSmartFilterConditionOperator.EXISTS;
    arguments: Array<string>;
}

export type FeedFilterNumberValues =
    | Array<string>
    | Array<number | undefined>
    | Array<NumberRangeInputValues>;

export type FeedFilterEnumValues = Array<string>;
export type FeedFilterStringValues = Array<string>;

export type FeedFilterOperationControlValues =
    | FeedFilterEnumValues
    | FeedFilterNumberValues
    | FeedFilterStringValues;

export interface GroupFilterInspectorState {
    adGroupId?: GroupId;
    filterId?: string;
    feedId?: string;
    filter: EditGroupFilterItem;
    isOpen: boolean;
    inspectorStatus: PageStatus;
    schema: FeedFilterSchema;
}

export type ProductsList = Record<string, ProductsListItem>;

export interface ProductsListItem {
    id: string;
    type: CampaignType;
    restrictions: ProductsListItemRestriction[];
}

export interface ProductsListItemRestriction {
    id: string;
    groupType: GroupType;
    showConditionType: CpmGroupShowConditionType;
    conditions: ProductsListItemRestrictionCondition[];
}

export interface ProductsListItemRestrictionCondition {
    name: ConditionsKeys;
    nestedConditions?: Array<
        ConditionsCorrectionsKeys | ConditionsSocialDemoKeys
    >;
}

export interface ProductsRestrictionGroupTypeItem {
    groupType: GroupType;
    restrictionId: string;
}

export enum ConditionsKeys {
    geo = 'geo',
    phrases = 'phrases',
    social_demo = 'social_demo',
    correction = 'correction',
    goal_stat = 'goal_stat',
    audio_genres = 'audio_genres',
    advert_pages_outdoor = 'advert_pages_outdoor',
    advert_pages_indoor = 'advert_pages_indoor'
}

export enum ConditionsCorrectionsKeys {
    device = 'device',
    weather = 'weather',
    socdem = 'socdem',
    target_audience = 'target_audience'
}

export enum ConditionsSocialDemoKeys {
    social_demo = 'social_demo',
    family = 'family',
    behaviors = 'behaviors',
    interests = 'interests',
    audience = 'audience'
}

export enum ConditionsCorrectionsApiKeys {
    correction_device = 'correction_device',
    correction_weather = 'correction_weather',
    correction_socdem = 'correction_socdem',
    correction_target_audience = 'correction_target_audience'
}

export function getEditGroupDataValueWithMinusPhrases(
    value: EditGroupDataValue
): EditTextGroupDataValue | EditPerformanceGroupDataValue | undefined {
    switch (value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
            return value;
        default:
            return;
    }
}
